/**
 * Home's Today and Email digests (logic/home), when the workspace has mail: the next events today and tomorrow, invites I
 * haven't answered, and the unread / needs-reply threads across my mailboxes. Each loads on its own after Home renders (Chat never waits on
 * them) and stays hidden when it fails. Realtime `calendar.event.*` / `mail.*` events refetch the affected digest;
 * a minute timer moves "now" forward (`tick`) and refetches when the day changes. Chat and Docs come from the sidebar
 * and the page tree, composed by `homeChat` / `homeSections`.
 *
 * The Email digest opens from the device (SnapshotStore: what it last read, or else Mail's inbox snapshot) and updates in
 * place; conversations new since then arrive with an accent (`newMailIds`). `settled` still waits for the server's
 * answer, so a digest from the device never says nothing needs me.
 */
import type { CalendarEvent, EventOccurrence, MailTabDef, MailTabSummary, MailThread, PersistentEvent } from "@bundle/contracts";
import { type HomeAgenda, type HomeInvite, type HomeMail, briefingRange, homeAgenda, homeInvites, homeMail } from "../logic/home.ts";
import { sameDay } from "../logic/calendar.ts";
import { cachedMailTabs, mailSnapshotKey, mailTabsKey, parseMailSnapshot } from "./MailboxViewModel.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** Mailboxes Home reads (my first ones; Mail itself lists them all). */
const MAILBOXES_MAX = 3;
/** Threads fetched per inbox to pick the digest from. */
const THREADS_PER_INBOX = 25;
/** How long a new conversation keeps its accent (Mail's new rows). */
const NEW_ROW_MS = 1600;

const HOME_SNAPSHOT_VERSION = 1;
export const homeMailSnapshotKey = (wsId: string, userId: string) => `mail-home.${wsId}.${userId}`;

type HomeInbox = { tabs: MailTabSummary[]; threads: MailThread[]; tabDefs?: MailTabDef[] | undefined };
/** The inboxes Home last read: the digest is worked out from them again, so a code that's expired since is gone. */
type HomeMailSnapshot = { v: number; inboxes: HomeInbox[] };

function parseHomeSnapshot(raw: string): HomeMailSnapshot | undefined {
  try {
    const v = JSON.parse(raw) as Partial<HomeMailSnapshot> | null;
    return v?.v === HOME_SNAPSHOT_VERSION && Array.isArray(v.inboxes) ? (v as HomeMailSnapshot) : undefined;
  } catch {
    return undefined;
  }
}

export type HomeViewState = {
  now: number;
  timeZone: string;
  /** undefined while loading, when mail is off, or when it failed. */
  agenda?: HomeAgenda;
  /** Invites I haven't answered, next INVITE_DAYS (the phone briefing's Calendar card); undefined like agenda. */
  invites?: HomeInvite[];
  mail?: HomeMail;
  /** Conversations a refresh just brought into the digest: they arrive with an accent, for a moment. */
  newMailIds: string[];
  /** Both digests have answered or failed, or there's no mail to wait for: until then Home can't say nothing needs me. */
  settled: boolean;
};

export class HomeViewModel extends ViewModel<HomeViewState> {
  private events: CalendarEvent[] = [];
  private occurrences: EventOccurrence[] = [];
  private agendaGen = 0;
  private mailGen = 0;
  private readonly answered = new Set<"agenda" | "mail">();
  private cancelTick: (() => void) | undefined;
  private cancelNew: (() => void) | undefined;

  constructor(private readonly ws: WorkspaceViewModel, private readonly mailEnabled: boolean, timeZone: string) {
    super({ now: ws.deps.clock.now(), timeZone, settled: !mailEnabled, newMailIds: [] });
    this.onDispose(() => {
      this.cancelTick?.();
      this.cancelNew?.();
    });
    if (mailEnabled) this.restoreMail();
  }

  /** The device's digest, if it has one: on screen at once. */
  private restoreMail(): void {
    const store = this.ws.deps.snapshots;
    if (store === undefined) return;
    const { wsId } = this.ws;
    const meId = this.ws.state.meId;
    const home = store.read(homeMailSnapshotKey(wsId, meId));
    const saved = home === undefined ? undefined : parseHomeSnapshot(home);
    const inbox = saved !== undefined ? undefined : store.read(mailSnapshotKey(wsId, meId));
    const list = inbox === undefined ? undefined : parseMailSnapshot(inbox);
    const inboxes: HomeInbox[] | undefined = saved?.inboxes ?? (list === undefined ? undefined : [{ tabs: list.tabs, threads: list.threads, tabDefs: cachedMailTabs(this.ws.deps.storage.get(mailTabsKey(wsId))) }]);
    if (inboxes !== undefined) this.update({ mail: homeMail(inboxes, undefined, this.ws.deps.clock.now()) });
  }

  private get api() {
    return this.ws.deps.api;
  }

  /** Fetches both digests (no-op without mail). */
  load(): void {
    if (!this.mailEnabled) return;
    void this.loadAgenda();
    void this.loadMail();
    this.schedule();
  }

  private schedule(): void {
    this.cancelTick?.();
    this.cancelTick = this.ws.deps.clock.setTimer(60_000, () => {
      this.tick();
      if (!this.isDisposed) this.schedule();
    });
  }

  /** Each minute: "now" moves (live events, ended ones drop off); a new day refetches. */
  tick(): void {
    const now = this.ws.deps.clock.now();
    const newDay = !sameDay(now, this.state.now, this.state.timeZone);
    this.update({ now });
    if (!this.mailEnabled) return;
    if (newDay) void this.loadAgenda();
    else this.deriveAgenda();
    if (this.state.mail?.threads.some((t) => t.codeExpiresAt !== undefined && t.codeExpiresAt <= now) === true) void this.loadMail();
  }

  onEvent(ev: PersistentEvent): void {
    if (!this.mailEnabled) return;
    if (ev.type === "calendar.event.updated" || ev.type === "calendar.event.removed") void this.loadAgenda();
    else if (ev.type === "mail.thread.updated" || ev.type === "mail.mailbox.updated" || ev.type === "mail.mailbox.deleted") void this.loadMail();
  }

  async loadAgenda(): Promise<void> {
    const gen = ++this.agendaGen;
    // Today and tomorrow for the agenda, and further ahead for invites to answer.
    const { from, to } = briefingRange(this.ws.deps.clock.now(), this.state.timeZone);
    try {
      const r = await this.api.listEvents(this.ws.wsId, from, to);
      if (gen !== this.agendaGen || this.isDisposed) return;
      this.events = r.events;
      this.occurrences = r.occurrences;
      this.update({ now: this.ws.deps.clock.now() });
      this.deriveAgenda();
      this.markAnswered("agenda");
    } catch {
      // Keep what we have (hidden until the first success).
      if (gen === this.agendaGen) this.markAnswered("agenda");
    }
  }

  async loadMail(): Promise<void> {
    const gen = ++this.mailGen;
    try {
      const boxes = (await this.api.listMailboxes(this.ws.wsId)).slice(0, MAILBOXES_MAX);
      const inboxes: HomeInbox[] = await Promise.all(
        boxes.map((m) => this.api.listMailThreads(this.ws.wsId, m.id, { folder: "inbox", limit: THREADS_PER_INBOX })),
      );
      if (gen !== this.mailGen || this.isDisposed) return;
      const before = this.state.mail?.threads;
      const mail = homeMail(inboxes, undefined, this.ws.deps.clock.now());
      this.update({ mail });
      this.markAnswered("mail");
      this.saveMail(inboxes);
      if (before !== undefined) {
        const newest = before.reduce((n, t) => Math.max(n, t.lastAt), 0);
        this.markNew(mail.threads.filter((t) => t.lastAt > newest).map((t) => t.id));
      }
    } catch {
      // Hidden until it loads.
      if (gen === this.mailGen) this.markAnswered("mail");
    }
  }

  private saveMail(inboxes: HomeInbox[]): void {
    const snapshot: HomeMailSnapshot = { v: HOME_SNAPSHOT_VERSION, inboxes: inboxes.map(({ tabs, threads, tabDefs }) => ({ tabs, threads, ...(tabDefs === undefined ? {} : { tabDefs }) })) };
    this.ws.deps.snapshots?.write(homeMailSnapshotKey(this.ws.wsId, this.ws.state.meId), () => JSON.stringify(snapshot));
  }

  private markNew(ids: string[]): void {
    if (ids.length === 0) return;
    this.update({ newMailIds: ids });
    this.cancelNew?.();
    this.cancelNew = this.ws.deps.clock.setTimer(NEW_ROW_MS, () => {
      this.cancelNew = undefined;
      this.update({ newMailIds: [] });
    });
  }

  private markAnswered(digest: "agenda" | "mail"): void {
    this.answered.add(digest);
    if (!this.isDisposed && !this.state.settled && this.answered.size === 2) this.update({ settled: true });
  }

  private deriveAgenda(): void {
    const { now, timeZone } = this.state;
    const meId = this.ws.state.meId;
    this.update({ agenda: homeAgenda(this.events, this.occurrences, now, timeZone, meId), invites: homeInvites(this.events, this.occurrences, now, meId) });
  }
}
